import React, { ComponentProps, Dispatch, SetStateAction } from 'react';
import { merge } from 'highcharts';
import { GetUserAssetResult, GetUserBrokerResultItem } from 'config/api';
import * as consts from 'config/consts';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { useScroll } from 'hooks/useScroll';

import { commaFormat } from 'components/features/new-report/utils/formatter';
import {
  UsePreviewProps,
  usePreview,
} from 'components/features/preview-share/Preview';
import {
  ChartPreviewState,
  RenderedChartToElement,
  StateController,
  defualtStateForHome,
  stateToOptions,
} from 'components/features/preview-share/PreviewUtils';
import {
  ChartForNormalProps,
  ChartForPreviewType,
} from 'components/features/preview-share/useChartRenderer';
import { NoSortingAssetsModal } from 'components/features/modal/NoSortingAssetsModal';
import { isNisaTypes } from 'components/features/utils/accountType';
import Loading from 'components/commons/loading/Loading';
import { isPremium, isIosApp } from 'components/features/utils/userStatus';
import { productTypeToAssetTypeKey } from 'components/features/utils/productTypes';
import {
  PortfolioRatioChartDatum,
  PortfolioRatioChartProps,
  PortfolioRatioStuffingProps,
  SelectedChartTypeProps,
  TypeKey,
  goToGrouping,
} from './PortfolioRatioStuffing';
import { PortfolioRatioChartInternal } from './PortfolioRatioChartInternal';
import { AccountPositionItemsProps } from './PortfolioRatioStuffingAccount';
import { PortfolioRatioStuffingList as PortfolioRatioList } from './PortfolioRatioStuffingList';
import { usePreviewPager } from '../PortfolioStuffing/asset/PortfolioStuffingAssetChart';
import { DataLoadingProps } from '../assetStuffing/AssetStuffing';
import { WithEmptyString } from '../WithEmptyView';
import { updateRoutigList } from '../Tab';

const getAccountType = (
  position: AccountPositionItemsProps,
  brokersInfo: GetUserBrokerResultItem[]
): string => {
  const brokerType = brokersInfo.find(
    brokers => brokers.Account === position.account
  )?.BrokerType;
  if (position.accountType === consts.AccountType.一般) {
    return 'General';
  } else if (position.accountType === consts.AccountType.特定) {
    return 'Identification';
  } else if (isNisaTypes(position.accountType)) {
    return 'Nisa';
  } else if (
    (position.accountType === consts.AccountType.iDeCo ||
      position.accountType === consts.AccountType.企業型) &&
    brokerType !== undefined &&
    brokerType === consts.BrokerType.確定拠出年金
  ) {
    return 'DefinedContributionPension';
  } else {
    return 'Other';
  }
};

const PortfolioRatioChartForPreview: (
  props: ComponentProps<ChartForPreviewType<PortfolioRatioChartProps['data']>>
) => React.ReactElement = ({ data, options, range, setChartDataURL }) => {
  return (
    <>
      <PortfolioRatioChartInternal
        {...{
          data,
          forPreviewSetter: { tag: 'forInPreview', setChartDataURL },
          options: range
            ? merge(options, {
                xAxis: { min: range.min.getTime(), max: range.max.getTime() },
              } as Highcharts.Options)
            : options,
        }}
      />
    </>
  );
};

const AssetsComment = () => {
  const [isShowModal, { hideModal, showModal }] = useModal();
  return (
    <>
      <div className="px-4">
        <div
          className="grid items-center p-4 mb-2 bg-bluegray-100 border border-bluegray-300 rounded-lg"
          style={{ gridTemplateColumns: 'auto 1fr' }}>
          <div>
            <img
              src="/static/images/pie-chart2.svg"
              alt="パイチャートのイメージ"
              width="60"
              height="60"
              className="w-full"
            />
          </div>
          <div className="ml-2 text-sm text-bluegray-700">
            <div className="font-bold">分散投資できているのかをチェック</div>
            <div>
              安定した運用を続けるために重要な分散投資。ここでは、投資信託やロボアドの投資先も分析して比率を確認できます。
            </div>
            <div
              className="flex flx-row items-center mt-4 text-gray-700 cursor-pointer"
              onClick={showModal}>
              <div>分類方法・分類されない資産について</div>
              <div className="icon-help_outline ml-1"></div>
            </div>
          </div>
        </div>
      </div>
      <NoSortingAssetsModal
        {...{
          isShow: isShowModal,
          hide: hideModal,
        }}
      />
    </>
  );
};

const PortfolioRatioChart = ({
  setRange,
  ...props
}: PortfolioRatioChartProps &
  ChartForNormalProps &
  DataLoadingProps): React.ReactElement | null => {
  const { data, isFetching, isRefetching } = props;

  return (
    <>
      {isFetching && isRefetching ? (
        <div className="bg-bluegray-600">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '102px',
              paddingBottom: '102px',
            }}
          />
        </div>
      ) : (
        <PortfolioRatioChartInternal
          {...{ data, forPreviewSetter: { tag: 'forGetRange', setRange } }}
        />
      )}
    </>
  );
};

type RefreshProps = {
  refresh: {
    isOutOfDate: boolean;
    account: string;
  } | null;
};

export type RawDataPositions = AccountPositionItemsProps & RefreshProps;

export type PortfolioRatioItemProps = {
  typeKey: TypeKey;
  ratio: string;
  amount: string;
  positions: RawDataPositions[];
};

type PortfolioRatioStuffingInternalProps = {
  selectedType: SelectedChartTypeProps;
  setSelectedType: Dispatch<SetStateAction<SelectedChartTypeProps>>;
  rawData: {
    typeKey: TypeKey;
    ratio: number | null;
    amount: number | null;
  }[];
  rawDataPositions: RawDataPositions[] | [];
  asset: GetUserAssetResult;
};

export const PortfolioRatioStuffingInternal = ({
  asset,
  brokersInfo,
  UserBrokerUpdateTime,
  assetBundleType,
  toPreview,
  backHome,
  selectedType,
  setSelectedType,
  rawData,
  rawDataPositions,
  isFetching,
  isRefetching,
}: Omit<PortfolioRatioStuffingProps, 'getIsOutOfDate'> &
  PortfolioRatioStuffingInternalProps &
  DataLoadingProps): React.ReactElement => {
  const { pushRouting } = useRouting();

  const getPositions = (typeKey: TypeKey) => {
    if (selectedType === 'products') {
      const availableBrokerArr = brokersInfo
        .filter(item => item.Available)
        .map(item => item.Account);

      const availablePositions = rawDataPositions.filter(item =>
        availableBrokerArr.includes(item.account)
      );

      return availablePositions
        .filter(position => productTypeToAssetTypeKey(position) === typeKey)
        .sort((first, second) => {
          const firstCurrentAmount = first.currentAmount ?? 0;
          const secondCurrentAmount = second.currentAmount ?? 0;
          return firstCurrentAmount < secondCurrentAmount ? 1 : -1;
        });
    } else if (selectedType === 'account') {
      return rawDataPositions
        .filter(position => getAccountType(position, brokersInfo) === typeKey)
        .sort((first, second) => {
          const firstCurrentAmount = first.currentAmount ?? 0;
          const secondCurrentAmount = second.currentAmount ?? 0;
          return firstCurrentAmount < secondCurrentAmount ? 1 : -1;
        });
    } else {
      return [];
    }
  };

  const data: PortfolioRatioItemProps[] = rawData
    .filter(
      item =>
        item.amount === null ||
        item.ratio === null ||
        item.amount < 0 ||
        item.ratio !== 0
    )
    .map(item => {
      if (item.amount === null || item.ratio === null)
        return {
          typeKey: item.typeKey,
          ratio: '---',
          amount: '---',
          positions: getPositions(item.typeKey),
        };

      if (
        item.typeKey === consts.AssetType['信用'] &&
        (item.ratio <= 0 || item.amount <= 0)
      )
        return {
          typeKey: item.typeKey,
          ratio: '-',
          amount: commaFormat(item.amount, { numberOfDecimals: 0 }),
          positions: getPositions(item.typeKey),
        };

      return {
        typeKey: item.typeKey,
        ratio: commaFormat(item.ratio, { numberOfDecimals: 2 }),
        amount: commaFormat(item.amount, { numberOfDecimals: 0 }),
        positions: getPositions(item.typeKey),
      };
    });

  const chartData = rawData
    .map(datum => {
      return {
        key: datum.typeKey,
        ratio: datum.ratio,
      };
    })
    .filter(
      item => item.ratio !== null && item.ratio > 0
    ) as PortfolioRatioChartDatum[];

  const props: UsePreviewProps<ChartPreviewState, typeof chartData> = {
    Chart: PortfolioRatioChartForPreview,
    defaultState: defualtStateForHome(asset),
    extra: chartData,
    RenderedChartToElement,
    StateController,
    stateToOptions,
    onCancel: backHome,
    chartTitle: 'ポートフォリオ',
  };

  const { preview, TwitterIconPreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  const changeTab = (selectedType: SelectedChartTypeProps): void => {
    setSelectedType(selectedType);
    updateRoutigList('portfolio', selectedType);
  };

  const TypeRadio = () => {
    return (
      <div className="flex justify-center pt-2 bg-bluegray-600 text-white">
        {isIosApp() ? (
          <div
            className="grid ml-4 w-2/3 text-xs border border-white rounded"
            style={{ gridTemplateColumns: '1fr auto 1fr' }}>
            <div
              className={`text-center py-1 ${
                selectedType === 'products' ? 'bg-white text-black' : ''
              } cursor-pointer`}
              onClick={(): void => changeTab('products')}>
              商品
            </div>
            <div
              className="my-1 border-r border-bluegray-500"
              style={{ borderWidth: '1px' }}></div>
            <div
              className={`text-center py-1 ${
                selectedType === 'account' ? 'bg-white text-black' : ''
              } cursor-pointer`}
              onClick={(): void => changeTab('account')}>
              口座
            </div>
          </div>
        ) : (
          <div
            className="grid ml-4 w-2/3 text-xs border border-white rounded"
            style={{ gridTemplateColumns: '1fr auto 1fr auto 1fr' }}>
            <div
              className={`text-center py-1 ${
                selectedType === 'products' ? 'bg-white text-black' : ''
              } cursor-pointer`}
              onClick={(): void => changeTab('products')}>
              商品
            </div>
            <div
              className="my-1 border-r border-bluegray-500"
              style={{ borderWidth: '1px' }}></div>
            <div
              className={`text-center py-1 ${
                selectedType === 'account' ? 'bg-white text-black' : ''
              } cursor-pointer`}
              onClick={(): void => changeTab('account')}>
              口座
            </div>
            <div
              className="my-1 border-r border-bluegray-500"
              style={{ borderWidth: '1px' }}></div>
            <div
              className={`flex flex-row items-center justify-center text-center py-1 ${
                selectedType === 'assets' ? 'bg-white text-black' : ''
              } cursor-pointer`}
              onClick={(): void => changeTab('assets')}>
              <div>資産分散</div>
              {!isPremium() && <div className="icon-lock ml-1 text-lock"></div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  useScroll();

  return (
    <>
      <WithEmptyString
        View={(): React.ReactElement => (
          <>
            <TypeRadio />
            <TwitterIconPreviewButton className="bg-bluegray-600" />
            <PortfolioRatioChart
              data={chartData}
              setRange={setRange}
              isFetching={isFetching}
              isRefetching={isRefetching}
            />
          </>
        )}
        empty={{
          message: '表示可能な資産がありません',
          className: 'mx-3',
          elementCustomizer: (): React.ReactElement => {
            return (
              <>
                <TypeRadio />
                <div className="grid justify-center bg-bluegray-600 min-h-60">
                  <img
                    src="/static/images/pai_chart.png"
                    alt="パイチャートのイメージ"
                    className="w-3/5 m-auto p-5"
                    height="140"
                    width="140"
                  />
                </div>
              </>
            );
          },
        }}
        isEmpty={
          (a => {
            return a;
          })(rawData.reduce((acc, v) => acc + (v.amount ?? 0), 0)) === 0
        }
      />
      <div className="flex flex-row items-center relative z-10 -mt-4 mb-4 bg-bluegray-600 text-white">
        {UserBrokerUpdateTime}
      </div>
      <WithEmptyString
        View={(): React.ReactElement => (
          <>
            {selectedType === 'assets' && <AssetsComment />}
            <div className="px-4">
              {data.map((p, i) => (
                <PortfolioRatioList
                  {...{ ...p, selectedType, brokersInfo }}
                  key={i}
                />
              ))}
            </div>
          </>
        )}
        empty={{
          message: '表示可能な資産がありません',
          className: 'mx-3',
        }}
        isEmpty={
          (a => {
            return a;
          })(rawData.reduce((acc, v) => acc + (v.amount ?? 0), 0)) === 0
        }
      />
      {assetBundleType.type === 'グループ資産' && (
        <div className="px-4">
          <div
            className="flex items-center justify-center text-base text-bluegray-600 font-bold leading-6 border border-bluegray-400 rounded-lg h-10 mt-6 bg-gray-50 cursor-pointer"
            onClick={goToGrouping(pushRouting)(`${assetBundleType.key}`)}>
            グループを編集
          </div>
        </div>
      )}
    </>
  );
};
